import type { FolderApi } from 'tweakpane';
import { Pane } from 'tweakpane';
import ls from 'localstorage-slim';
import type { BindingApi, BladeState, ButtonApi, FolderParams } from '@tweakpane/core';

type Position = {
  x: 'left' | 'right';
  y: 'top' | 'bottom';
};

type DevToolsConfig = {
  position?: Position;
};

export class DevToolsPlugin extends Pane {
  public readonly name = 'DevTools';

  protected container: HTMLDivElement;

  constructor(config?: DevToolsConfig) {
    const container = document.createElement('div');

    super({
      title: 'Dev Tools',
      expanded: false,
      container,
    });

    this.container = container;
    document.body.appendChild(this.container);

    container.style.zIndex = '1000';
    container.style.userSelect = 'none';

    this.setPosition(config?.position);

    this.element.querySelector('.tp-rotv_b')?.addEventListener('click', () => {
      this.saveState();
    });
  }

  public setPosition(position?: Position): void {
    this.container.style.position = 'fixed';
    this.container.style.top = '3px';
    this.container.style.maxHeight = 'calc(100vh - 6px)';
    this.container.style.overflowY = 'auto';
    this.container.style.left = '3px';

    switch (position?.x) {
      case 'left':
        this.container.style.left = '0';
        this.container.style.right = '';
        break;
      case 'right':
        this.container.style.right = '0';
        this.container.style.left = '';
        break;
      default:
        break;
    }

    switch (position?.y) {
      case 'top':
        this.container.style.top = '0';
        this.container.style.bottom = '';
        break;
      case 'bottom':
        this.container.style.bottom = '0';
        this.container.style.top = '';
        break;
      default:
        break;
    }
  }

  protected loadState(): void {
    if (!ls.get('DevTools')) return;

    const state: BladeState | null = ls.get('DevTools');

    if (state) {
      this.importState(state);
      this.refresh();
    }
  }

  protected saveState(): void {
    const state = this.exportState();
    ls.set('DevToolsPlugin', state);
  }

  public override addFolder(params: FolderParams): FolderApi {
    const folder = super.addFolder(params);
    folder.element.addEventListener('click', () => {
      this.saveState();
    });
    return folder;
  }

  public addSection(config: SectionConfig): {
    section: FolderApi;
    fields: FieldsAPI;
    selectors: SelectorsAPI;
  } {
    const section = this.addFolder({
      title: config.title,
      expanded: config.expanded,
    });

    const fields: FieldsAPI = {};
    const selectors: SelectorsAPI = {};

    Object.keys(config.fields).forEach((fieldID) => {
      const field = config.fields[fieldID];

      switch (field.type) {
        case 'select':
          // eslint-disable-next-line no-case-declarations
          const options: {
            options: Record<string, string>;
          } = {
            options: {
              [field.title]: '',
            },
          };

          Object.assign(options.options, field.options);

          // eslint-disable-next-line no-case-declarations
          const select = { [fieldID]: '' };

          selectors[fieldID] = {
            set: (val: string): void => {
              select[fieldID] = val;
              this.refresh();
              this.saveState();
            },
          };

          fields[fieldID] = section.addBinding(select, fieldID, options).on('change', ({ value }) => {
            field.cb?.(value);
          });
          break;
        case 'button':
          fields[fieldID] = section.addButton({ title: field.title }).on('click', () => {
            field.cb?.('');
          });
          break;
        case 'checkbox':
          fields[fieldID] = section
            .addBinding({ [fieldID]: field.selected }, fieldID, {
              label: field.title,
            })
            .on('change', ({ value }) => {
              field.cb?.(value ? value.toString() : '');
            });
          break;
        default:
          break;
      }
    });

    return {
      section,
      fields,
      selectors,
    };
  }

  public setSafeArea(safeArea?: { top: number; right: number; bottom: number; left: number }): void {
    this.container.style.paddingTop = `${safeArea?.top}px`;
    this.container.style.paddingRight = `${safeArea?.right}px`;
    this.container.style.paddingBottom = `${safeArea?.bottom}px`;
    this.container.style.paddingLeft = `${safeArea?.left}px`;
  }
}

export type SelectorsAPI = Record<
  string,
  {
    set: (val: string) => void;
  }
>;

export type FieldsAPI = Record<string, BindingApi | ButtonApi>;

type DebugFieldType = 'button' | 'select' | 'checkbox';

export type DebugFieldOptions = Record<string, string>;

export type DebugFields = Record<
  string,
  {
    type: DebugFieldType;
    title: string;
    selected?: boolean;
    options?: DebugFieldOptions;
    cb?: (value: string) => void;
  }
>;

export type SectionConfig = {
  title: string;
  expanded?: boolean;
  fields: DebugFields;
};
